import { provideHttpClient, withInterceptors } from '@angular/common/http';
import * as i0 from '@angular/core';
import { makeEnvironmentProviders, APP_INITIALIZER, inject, Injector, runInInjectionContext, Injectable } from '@angular/core';
import { createConfigTokens, matchUrl } from '@odx/angular/utils';
import { BehaviorSubject, filter, map, distinctUntilChanged, shareReplay, firstValueFrom, isObservable, from, of, take, switchMap } from 'rxjs';
import { Logger } from '@odx/angular/internal';
import { WindowRef } from '@odx/angular';

const DEFAULT_AVAILABLE_LANGUAGES = ['en-US', 'en-GB', 'en', 'de-DE', 'de', 'es-ES', 'es', 'fr-FR', 'fr-CA', 'fr'];
/**
 * Utilizes `createConfigTokens` to generate configuration tokens and related utilities for the
 * localization settings within the application. It defines a structured and type-safe approach
 * for managing localization configurations, such as available languages, and potentially
 * a dynamic loader for the active language. The generated tokens and utilities facilitate accessing
 * and providing these configurations throughout the application.
 *
 * @const {InjectionToken<LocalizationConfig>} LocalizationConfig - An InjectionToken for accessing
 * the localization configuration within Angular's dependency injection framework. Use this token to inject
 * localization configurations into services, components, or other tokens.
 *
 * @const {LocalizationConfig} LocalizationDefaultConfig - The default configuration values for localization,
 * based on the provided settings. Includes the default available languages.
 *
 * @const {Function} injectLocalizationConfig - A utility function that returns the injected value of
 * `LocalizationConfig` token. Use this function to access the current localization configuration in
 * a type-safe manner.
 *
 * @const {Function} provideLocalizationConfig - A utility function to provide the `LocalizationConfig` token
 * with specific values. Use this function to configure and override default localization settings within
 * an Angular module or component.
 *
 * @example
 * ```ts
 * // Providing localization settings in an Angular module
 * @NgModule({
 *   providers: [
 *     provideLocalizationConfig({
 *       availableLanguages: ['en', 'es', 'fr'],
 *     })
 *   ]
 * })
 * export class AppModule { }
 *
 * // Injecting and using the localization configuration in a service
 * @Injectable({ providedIn: 'root' })
 * export class MyLocalizationService {
 *   constructor(@Inject(LocalizationConfig) private config: LocalizationConfig) {
 *     console.log('Available languages:', config.availableLanguages);
 *   }
 * }
 * ```
 */
const { LocalizationConfig, LocalizationDefaultConfig, injectLocalizationConfig, provideLocalizationConfig } = createConfigTokens('Localization', '@odx/angular/internal/translate', {
    availableLanguages: DEFAULT_AVAILABLE_LANGUAGES,
});
/**
 * Sets up the localization configuration for the application and initializes the localization
 * system with the provided configuration and features. This function is typically called
 * within the main module or a core module of the application to ensure localization settings
 * are available and active as early as possible.
 *
 * @param {Partial<LocalizationConfig>} config The localization configuration settings, including
 * available languages and optionally, a custom loader for the active language.
 * @param {...LocalizationFeature[]} features Additional localization features or configurations
 * to be registered as part of the application's environment providers.
 * @returns {EnvironmentProviders} A collection of providers for setting up the application's
 * localization environment, to be used with Angular's environment-specific injector configuration.
 * @example
 * ```ts
 * // Example of providing localization settings in an Angular module
 * @NgModule({
 *   imports: [...],
 *   providers: [
 *     provideLocalization({
 *       availableLanguages: ['en', 'es', 'fr'],
 *       activeLanguageLoader: () => fetch('/api/user/language').then(res => res.text()),
 *     }),
 *   ],
 * })
 * export class AppModule {}
 * ```
 */
function provideLocalization(config, ...features) {
    return makeEnvironmentProviders([
        provideLocalizationConfig(config),
        {
            provide: APP_INITIALIZER,
            useFactory: (localizationService) => () => localizationService.loadActiveLanguage(),
            deps: [LocalizationService],
            multi: true,
        },
        features,
    ]);
}

var logger = new Logger('@odx/angular/localization');

class LocalizationService {
    constructor() {
        this.localizationConfig = injectLocalizationConfig();
        this.injector = inject(Injector);
        this.localeState$ = new BehaviorSubject(new Intl.Locale(this.localizationConfig.availableLanguages[0]));
        /**
         * Represents an observable stream of the active language in the localization service.
         * The active language is determined by the locale state.
         *
         * @emits {string} The active language.
         */
        this.activeLanguage$ = this.localeState$.pipe(filter(Boolean), map((x) => x.baseName), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        /**
         * Gets the list of available languages for localization.
         *
         * @type {string[]} The list of available languages.
         */
        this.availableLanguages = this.localizationConfig.availableLanguages;
        /** Default language derived from the first entry in the available languages list.
         *
         * @type {string}
         */
        this.defaultLanguage = this.availableLanguages[0];
    }
    /**
     * Retrieves the currently active language. Falls back to the default language if
     * no active language is set.
     *
     * @returns {string} Full locale name e.g. 'en-US' or just 'en' if no region info is attached.
     */
    getActiveLanguage() {
        return this.localeState$.getValue()?.baseName ?? new Intl.Locale(this.defaultLanguage).baseName;
    }
    /**
     * Retrieves the active language subtag.
     *
     * @returns {string} The two-digit language subtag, e.g. 'en', 'fr'.
     */
    getActiveLanguageSubtag() {
        return this.localeState$.getValue()?.language ?? new Intl.Locale(this.defaultLanguage).language;
    }
    /**
     * Sets the active language based on the provided language code. If the language is not
     * recognized, it logs an error and does not change the active language.
     *
     * @param {string} language The language code to set as the active language.
     */
    setActiveLanguage(language) {
        try {
            const locale = new Intl.Locale(language);
            if (this.availableLanguages.includes(locale.baseName) || this.availableLanguages.includes(locale.language)) {
                this.localeState$.next(locale);
            }
            else {
                throw new Error(`Unrecognizable language '${language}'. Make sure it's a part of availableLanguages inside LocalizationService config.`);
            }
        }
        catch (error) {
            logger.error(error);
        }
    }
    /**
     * Loads the active language for the localization service.
     * This method runs in an injection context and sets the active language based on the result of the active language loader.
     * If the active language loader fails, the default language is set as the active language.
     *
     * @returns {Promise<void>} A promise that resolves when the active language is loaded and set.
     */
    async loadActiveLanguage() {
        return runInInjectionContext(this.injector, async () => {
            const activeLanguageLoader$ = this.createActiveLanguageLoader$();
            const activeLanguage = await firstValueFrom(activeLanguageLoader$).catch(() => null);
            this.setActiveLanguage(activeLanguage ?? this.defaultLanguage);
        });
    }
    createActiveLanguageLoader$() {
        const language = this.localizationConfig.activeLanguageLoader?.();
        if (isObservable(language)) {
            return language;
        }
        if (language instanceof Promise) {
            return from(language);
        }
        return of(language);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LocalizationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LocalizationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LocalizationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * @internal
 * Creates a feature provider for the localization module.
 *
 * @param {Provider[] | EnvironmentProviders} provider - The providers to be used for the localization feature.
 * @returns {LocalizationFeature} The localization feature provider.
 */
function makeLocalizationFeature(provider) {
    return provider;
}

const HTTP_LANGUAGE_HEADER = 'Accept-Language';
/**
 * Creates an HTTP interceptor function that adds a language header to outgoing HTTP requests,
 * but only for URLs matching the provided criteria. This allows the server to respond with
 * resources localized in the user's current language.
 *
 * @param {HttpLanguageHeaderInterceptorConfig} config - The configuration for the interceptor,
 * including the list of allowed URLs and optionally a custom header name.
 * @returns {HttpInterceptorFn} An Angular HTTP interceptor function that conditionally adds
 * the current language as an HTTP header based on the request URL.
 *
 * @example
 * ```ts
 * // Use in HTTP client module setup
 * import { HttpClientModule } from '@angular/common/http';
 * import { httpLanguageHeaderInterceptor, HTTP_LANGUAGE_HEADER } from '@odx/angular/localization';
 *
 * @NgModule({
 *   imports: [HttpClientModule],
 *   providers: [
 *     provideHttpClient(withInterceptors([
 *       httpLanguageHeaderInterceptor({ allowedUrls: ['/api'], headerName: HTTP_LANGUAGE_HEADER })
 *     ]))
 *   ]
 * })
 * export class AppModule {}
 * ```
 */
function httpLanguageHeaderInterceptor({ allowedUrls, headerName }) {
    return (req, next) => {
        const language$ = inject(LocalizationService).activeLanguage$.pipe(take(1));
        for (const allowedUrl of allowedUrls) {
            if (!matchUrl(req.url, allowedUrl))
                continue;
            return language$.pipe(switchMap((language) => next(req.clone({ setHeaders: { [headerName ?? HTTP_LANGUAGE_HEADER]: language } }))));
        }
        return next(req);
    };
}
/**
 * Provides a localization feature that automatically adds a language header to HTTP requests
 * for specified URLs, using the current active language. This function simplifies setting up
 * the HTTP language header interceptor by wrapping it into a LocalizationFeature, making it easy
 * to integrate with the application's localization infrastructure.
 *
 * @param {HttpLanguageHeaderInterceptorConfig} config - The configuration for the language header
 * interceptor, specifying which URLs should include the language header and optionally defining a
 * custom header name.
 * @returns {LocalizationFeature} A localization feature object that can be used to configure
 * the application's localization settings to include automatic language header addition for HTTP requests.
 *
 * @example
 * ```ts
 * // Example usage with the localization provider
 * import { provideLocalization, LocalizationModule, withHttpLanguageHeader } from '@odx/angular/localization';
 *
 * @NgModule({
 *   imports: [LocalizationModule],
 *   providers: [
 *     provideLocalization({}, withHttpLanguageHeader({ allowedUrls: ['/api'] }))
 *   ]
 * })
 * export class AppModule {}
 * ```
 */
function withHttpLanguageHeader(config) {
    const providers = provideHttpClient(withInterceptors([httpLanguageHeaderInterceptor(config)]));
    return makeLocalizationFeature(providers);
}

const HTTP_LANGUAGE_QUERY = 'language';
/**
 * Creates an HTTP interceptor function that appends the current language as a query parameter
 * to outgoing HTTP requests that match specified URLs. This interceptor enables dynamic content
 * localization by informing the backend of the user's preferred language.
 *
 * @param {HttpLanguageQueryParamInterceptorConfig} config - Configuration for the interceptor,
 * including allowed URLs and an optional custom name for the query parameter.
 * @returns {HttpInterceptorFn} The interceptor function ready to be applied to the HTTP client.
 *
 * @example
 * ```ts
 * // Applying the interceptor in an Angular module
 * import { HttpClientModule } from '@angular/common/http';
 * import { withHttpLanguageQueryParam } from '@odx/angular/localization';
 *
 * @NgModule({
 *   imports: [HttpClientModule],
 *   providers: [
 *     provideHttpClient(withInterceptors([
 *       withHttpLanguageQueryParam({ allowedUrls: ['/api/resources'] })
 *     ]))
 *   ]
 * })
 * export class ApiModule {}
 * ```
 */
function withHttpLanguageQueryParamInterceptor({ allowedUrls, queryParamName }) {
    return (req, next) => {
        const language$ = inject(LocalizationService).activeLanguage$.pipe(take(1));
        for (const allowedUrl of allowedUrls) {
            if (!matchUrl(req.url, allowedUrl))
                continue;
            return language$.pipe(switchMap((language) => next(req.clone({ setParams: { [queryParamName ?? HTTP_LANGUAGE_QUERY]: language } }))));
        }
        return next(req);
    };
}
/**
 * Registers the HTTP interceptor that appends the current language as a query parameter to
 * outgoing requests for configured URLs as a localization feature. This utility function
 * simplifies the setup by bundling the interceptor into a feature that can be easily integrated
 * with localization configurations.
 *
 * @param {HttpLanguageQueryParamInterceptorConfig} config - Configuration for appending the
 * language query parameter, including allowed URLs and an optional parameter name.
 * @returns {LocalizationFeature} A localization feature encapsulating the interceptor, ready
 * for integration with the application's localization setup.
 *
 * @example
 * ```ts
 * // Integrating the localization feature in an Angular module
 * import { provideLocalization } from '@angular/core';
 * import { withHttpLanguageQueryParam } from '@odx/angular/localization';
 *
 * @NgModule({
 *   providers: [
 *     provideLocalization({}, withHttpLanguageQueryParam({ allowedUrls: ['/api'] }))
 *   ]
 * })
 * export class AppModule {}
 * ```
 */
function withHttpLanguageQueryParam(config) {
    const providers = provideHttpClient(withInterceptors([withHttpLanguageQueryParamInterceptor(config)]));
    return makeLocalizationFeature(providers);
}

/**
 * Asynchronously loads the browser's current language setting by utilizing the `WindowRef` service.
 * This function is intended to be used as a dynamic language loader within a localization configuration,
 * allowing applications to automatically adapt to the user's preferred language as specified in their
 * browser settings.
 *
 * The `WindowRef` service abstracts window-related operations, making it easier to interact with global
 * browser properties and methods in a testable and platform-agnostic way. This function makes use of
 * `WindowRef` to access the navigator's language setting in a safe and Angular-friendly manner.
 *
 * @returns {Promise<string>} A promise that resolves to the language code (e.g., 'en', 'fr') of the
 * browser's currently configured language. This can then be used to set the active language in a
 * localization configuration.
 *
 * @example
 * ```ts
 * // Example usage in a localization configuration
 * import { provideLocalization } from '@odx/angular/utils';
 * import { browserLanguageLoader } from '@odx/angular/localization';
 *
 * @NgModule({
 *   providers: [
 *     provideLocalization({
 *       activeLanguageLoader: browserLanguageLoader,
 *       availableLanguages: ['en', 'es', 'fr'],
 *       availableLocales: ['en-US', 'es-ES', 'fr-FR']
 *     })
 *   ]
 * })
 * export class AppModule { }
 * ```
 */
const browserLanguageLoader = async () => {
    return inject(WindowRef).getLanguage();
};

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_AVAILABLE_LANGUAGES, HTTP_LANGUAGE_HEADER, HTTP_LANGUAGE_QUERY, LocalizationConfig, LocalizationDefaultConfig, LocalizationService, browserLanguageLoader, httpLanguageHeaderInterceptor, injectLocalizationConfig, makeLocalizationFeature, provideLocalization, provideLocalizationConfig, withHttpLanguageHeader, withHttpLanguageQueryParam, withHttpLanguageQueryParamInterceptor };
